import { Button, EmptyState, Skeleton, useLoadingTimeout } from '@citizen-walk/shared-ui';
import { useMemo, useState } from 'react';
import type { ColorRule, FieldDef } from '@citizen-walk/shared-types';
import { applyPostFilters } from './applyPostFilters';
import { ColorBadge, evaluatePostColor, useDistrictColorRule } from './ColorBadge';
import { FilterPanel, type FilterSessionOption } from './FilterPanel';
import {
  buildGroupOptions,
  SORT_OPTIONS,
  groupPosts,
  sortPosts,
  type GroupKey,
  type SortKey,
} from './groupPosts';
import { PostThumb } from './PostThumb';
import { useReportAccessDeniedOnError } from '../district';
import type { MainFilters } from './useMainFilters';
import { useAgree } from './useAgree';
import type { AiCategoryMastersResult } from './useAiCategoryMasters';
import type { PostMinimal, PostsState } from './usePosts';

/**
 * AppHome の「一覧」タブパネル。
 *
 * Phase F (old2 `apps/user/src/pages/tabs/ListTab.tsx` 逐語移植) で全面拡張:
 *   - グループ化 = なし / 日付 / セッション + 入力項目のうち分析軸に使う項目 (buildGroupOptions)
 *     + groupPosts + SectionHeader (折りたたみ件数表示)
 *   - SORT_OPTIONS 7 種 (新しい / 古い / そう思う！ + ECW 4 種)
 *   - list / card view 切替 + card 写真サイズ slider (80-300px)
 *   - 🔍 フィルタ button → 全画面 FilterPanel (old2 同一 section)
 *
 * filter state は AppHome 側で `useMainFilters` に hoist し props 経由で受け取る controlled component。
 * 仮想リスト (@tanstack/react-virtual) は依存未追加のため通常リストで実装 (old2 と同等の体感、
 * 30 人規模 = 数百件で十分。万件規模になったら virtualizer 導入)。
 */

type ViewMode = 'list' | 'card';

function formatDate(p: PostMinimal): string {
  if (!p.createdAt) return '送信中';
  return p.createdAt.toDate().toLocaleString('ja-JP', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface ListPanelProps {
  postsState: PostsState;
  categoryMasters: AiCategoryMastersResult;
  /** Phase F: 集約 filter state (old2 FilterContext 相当)。 */
  filters: MainFilters;
  /** FilterPanel の開閉 (header の 🔍 button から制御)。 */
  filterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
  /** 現セッション (現セッションのみ filter + group 名解決)。 */
  currentSessionId: string | null;
  /** session chip / group 名解決用。 */
  sessions: FilterSessionOption[];
  /** District mode (ECW sort/group の出し分け)。 */
  mode: string | null;
  /** FilterPanel の postFields / userFields / colorRule。 */
  postFields: FieldDef[];
  userFields: FieldDef[];
  colorRule: ColorRule | null;
  onDetailPost: (post: PostMinimal) => void;
  /** agree button 用 (旧 ListTab L137 useAuth 経由)。 */
  currentUid: string | null;
  /** R-70: onlyMine で「自分」とみなす全 uid (現 uid + 端末引き継ぎ元)。省略時は currentUid 単独。 */
  selfUids?: string[];
  /** agrees 書込対象 path。 */
  districtId: string;
  /** 30s timeout 時の retry callback。 */
  onRetry?: () => void;
}

export function ListPanel({
  postsState,
  categoryMasters,
  filters,
  filterOpen,
  onFilterOpenChange,
  currentSessionId,
  sessions,
  mode,
  postFields,
  userFields,
  colorRule: colorRuleProp,
  onDetailPost,
  currentUid,
  selfUids,
  districtId,
  onRetry,
}: ListPanelProps) {
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [groupKey, setGroupKey] = useState<GroupKey>('none');
  // 既定はカード表示 (写真サムネ付きで投稿が一覧しやすい)。リスト表示はトグルで選択可能。
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [photoSize, setPhotoSize] = useState(160);
  // docs/105: 地区 colorRule (kind 形式)。null なら各行 / カードにバッジを出さない。
  const fallbackColorRule = useDistrictColorRule();
  const colorRule = colorRuleProp ?? fallbackColorRule;
  const loadTimedOut = useLoadingTimeout(postsState.status === 'loading');
  // 地区「強い停止」Phase 1: 一覧でも permission-denied を非公開シグナルとして通知 (MapPage と同様、
  // DistrictGate が「現在非公開中」画面へ切替える)。
  useReportAccessDeniedOnError(postsState.status === 'error' ? postsState.error : undefined);

  const isEcw = mode === 'ecw';
  const sortOpts = SORT_OPTIONS.filter((o) => !o.ecwOnly || isEcw);
  const groupOpts = useMemo(() => buildGroupOptions(postFields), [postFields]);
  // 地区の入力項目が変わって選択中のグループが無くなったら「なし」に戻す。
  const effectiveGroupKey: GroupKey = groupOpts.some((o) => o.key === groupKey) ? groupKey : 'none';

  const sessionMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sessions) m.set(s.id, s.name);
    return m;
  }, [sessions]);

  const allPosts = postsState.status === 'ready' ? postsState.posts : [];
  const { filtered, filterActive, activeFilterCount } = applyPostFilters(
    allPosts,
    filters.toFilterState(currentSessionId, currentUid, selfUids),
  );
  const sorted = useMemo(() => sortPosts(filtered, sortKey), [filtered, sortKey]);
  const groups = useMemo(
    () => groupPosts(sorted, effectiveGroupKey, sessionMap, postFields),
    [sorted, effectiveGroupKey, sessionMap, postFields],
  );

  if (postsState.status === 'loading' && !loadTimedOut) {
    return (
      <div
        className="p-3 space-y-2"
        role="status"
        aria-busy="true"
        aria-label="投稿一覧を読込中"
        data-testid="list-panel-skeleton"
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex items-start gap-3 bg-white border border-slate-200 rounded p-3"
          >
            <Skeleton variant="rect" width={64} height={64} />
            <div className="flex-1 space-y-2 pt-1">
              <Skeleton variant="text" width="60%" height={14} />
              <Skeleton variant="text" width="40%" height={12} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (postsState.status === 'loading' && loadTimedOut) {
    return (
      <div className="p-4 space-y-2">
        <p className="text-sm text-warning-700">
          読込が遅れています。ネットワーク状況を確認のうえ、再試行してください。
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => (onRetry ? onRetry() : window.location.reload())}
        >
          再試行
        </Button>
      </div>
    );
  }
  if (postsState.status === 'error') {
    return (
      <div className="p-4 space-y-2">
        <p className="text-sm text-danger-600">読込エラー: {postsState.error.message}</p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => (onRetry ? onRetry() : window.location.reload())}
        >
          再試行
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* ツールバー: sort + group + view mode + photo slider (old2 ListTab L210-249 踏襲) */}
      <div className="bg-white border-b border-slate-200 px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          aria-label="並び替え"
          className="min-h-[32px] px-2 border border-slate-300 rounded bg-white"
        >
          {sortOpts.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={effectiveGroupKey}
          onChange={(e) => setGroupKey(e.target.value as GroupKey)}
          aria-label="グループ化"
          className="min-h-[32px] px-2 border border-slate-300 rounded bg-white"
        >
          {groupOpts.map((o) => (
            <option key={o.key} value={o.key}>
              {`グループ: ${o.label}`}
            </option>
          ))}
        </select>
        <div
          className="flex border border-slate-300 rounded overflow-hidden"
          role="group"
          aria-label="表示モード"
        >
          <button
            type="button"
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            className={`px-2 py-1 min-h-[32px] text-sm ${
              viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-white text-slate-600'
            }`}
            title="リスト表示"
          >
            ≡
          </button>
          <button
            type="button"
            onClick={() => setViewMode('card')}
            aria-pressed={viewMode === 'card'}
            className={`px-2 py-1 min-h-[32px] text-sm ${
              viewMode === 'card' ? 'bg-primary-600 text-white' : 'bg-white text-slate-600'
            }`}
            title="カード表示"
          >
            ▦
          </button>
        </div>
        <button
          type="button"
          onClick={() => onFilterOpenChange(true)}
          aria-label="検索・フィルター"
          className={`px-2 py-1 min-h-[32px] rounded border text-xs relative ${
            filterActive
              ? 'bg-primary-50 text-primary-700 border-primary-300'
              : 'bg-white text-slate-600 border-slate-300'
          }`}
        >
          🔍 フィルター
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-danger-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
        {viewMode === 'card' && (
          <label className="flex items-center gap-1 ml-auto">
            <span className="text-slate-500">写真</span>
            <input
              type="range"
              min={80}
              max={300}
              step={10}
              value={photoSize}
              onChange={(e) => setPhotoSize(Number(e.target.value))}
              aria-label="写真サイズ"
              className="w-20"
            />
          </label>
        )}
      </div>

      {sorted.length === 0 ? (
        filterActive ? (
          <EmptyState
            icon="🔍"
            title="該当する投稿がありません"
            description="フィルターを変更すると、他の投稿が表示される可能性があります。"
            action={{
              label: 'フィルターをリセット',
              variant: 'secondary',
              onClick: filters.reset,
            }}
          />
        ) : (
          <EmptyState
            icon="📭"
            title="まだ投稿がありません"
            description="気になる場所を見つけたら、最初の投稿を作成してみましょう。"
          />
        )
      ) : viewMode === 'list' ? (
        groups.map((g, gi) => (
          <div key={gi}>
            {g.label && <SectionHeader label={g.label} count={g.posts.length} />}
            <ul className="divide-y divide-slate-200">
              {g.posts.map((p) => (
                <ListRow
                  key={p.id}
                  post={p}
                  uid={currentUid}
                  districtId={districtId}
                  colorRule={colorRule}
                  onSelect={() => onDetailPost(p)}
                />
              ))}
            </ul>
          </div>
        ))
      ) : (
        <div className="p-3">
          {groups.map((g, gi) => (
            <div key={gi}>
              {g.label && <SectionHeader label={g.label} count={g.posts.length} />}
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
              >
                {g.posts.map((p) => (
                  <CardItem
                    key={p.id}
                    post={p}
                    uid={currentUid}
                    districtId={districtId}
                    colorRule={colorRule}
                    photoSize={photoSize}
                    onSelect={() => onDetailPost(p)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <FilterPanel
        open={filterOpen}
        onClose={() => onFilterOpenChange(false)}
        activeFilterCount={activeFilterCount}
        onReset={filters.reset}
        keyword={filters.keyword}
        onKeywordChange={filters.setKeyword}
        sessions={sessions}
        sessionIds={filters.sessionIds}
        onSessionIdsChange={filters.setSessionIds}
        dateFrom={filters.dateFrom}
        onDateFromChange={filters.setDateFrom}
        dateTo={filters.dateTo}
        onDateToChange={filters.setDateTo}
        hasPhoto={filters.hasPhoto}
        onHasPhotoChange={filters.setHasPhoto}
        minAgreeCount={filters.minAgreeCount}
        onMinAgreeCountChange={filters.setMinAgreeCount}
        postFieldFilters={filters.postFieldFilters}
        onPostFieldFiltersChange={filters.setPostFieldFilters}
        userFieldFilters={filters.userFieldFilters}
        onUserFieldFiltersChange={filters.setUserFieldFilters}
        selectedCategoryCodes={filters.selectedCategoryCodes}
        onSelectedCategoryCodesChange={filters.setSelectedCategoryCodes}
        postFields={postFields}
        userFields={userFields}
        colorRule={colorRule}
        categoryMasters={categoryMasters}
      />
    </div>
  );
}

function SectionHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-600 flex items-center justify-between sticky top-0 z-[1]">
      <span>{label}</span>
      <span className="text-slate-400">{count}件</span>
    </div>
  );
}

function ListRow({
  post: p,
  uid,
  districtId,
  colorRule,
  onSelect,
}: {
  post: PostMinimal;
  uid: string | null;
  districtId: string;
  colorRule: ColorRule | null;
  onSelect: () => void;
}) {
  const colorInfo = evaluatePostColor(p, colorRule);
  return (
    <li className="flex items-start gap-3 p-4 hover:bg-slate-50">
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 min-w-0 text-left flex gap-3 items-start bg-transparent border-none p-0 cursor-pointer"
      >
        {p.photoThumbPath && (
          <div className="w-16 h-16 shrink-0">
            <PostThumb
              path={p.photoThumbPath}
              url={p.photoThumbUrl}
              alt=""
              projection={p.photo?.projection ?? null}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-slate-900 truncate flex items-center gap-1.5">
            <span className="truncate">{p.title}</span>
            {p.visibility === 'hidden_others' && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0">
                🔒 自分のみ
              </span>
            )}
          </div>
          {colorInfo && <ColorBadge evaluated={colorInfo} variant="dot" className="mt-0.5" />}
          <div className="text-xs text-slate-400 mt-1 truncate">
            {p.authorNickname} · {formatDate(p)}
          </div>
        </div>
      </button>
      <AgreeButton districtId={districtId} postId={p.id} uid={uid} initialCount={p.agreeCount} />
    </li>
  );
}

function CardItem({
  post: p,
  uid,
  districtId,
  colorRule,
  photoSize,
  onSelect,
}: {
  post: PostMinimal;
  uid: string | null;
  districtId: string;
  colorRule: ColorRule | null;
  photoSize: number;
  onSelect: () => void;
}) {
  const colorInfo = evaluatePostColor(p, colorRule);
  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden shadow-sm flex flex-col">
      <button
        type="button"
        onClick={onSelect}
        className="text-left bg-transparent border-none p-0 cursor-pointer"
      >
        {p.photoThumbPath && (
          <div className="w-full bg-slate-50" style={{ height: photoSize }}>
            {/* カードは写真全体を見せる (contain)。サイズは photoSize slider 連動 (PostThumb が box 充填)。 */}
            <PostThumb
              path={p.photoThumbPath}
              url={p.photoThumbUrl}
              alt=""
              fit="contain"
              projection={p.photo?.projection ?? null}
            />
          </div>
        )}
        <div className="p-2">
          {colorInfo && <ColorBadge evaluated={colorInfo} variant="dot" className="mb-1" />}
          <div className="font-medium text-sm text-slate-900 truncate flex items-center gap-1">
            {p.visibility === 'hidden_others' && (
              <span className="text-[10px] shrink-0" title="自分のみ">
                🔒
              </span>
            )}
            <span className="truncate">{p.title}</span>
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5 truncate">
            {p.authorNickname} · {formatDate(p)}
          </div>
        </div>
      </button>
      <div className="px-2 pb-2 mt-auto">
        <AgreeButton
          districtId={districtId}
          postId={p.id}
          uid={uid}
          initialCount={p.agreeCount}
          fullWidth
        />
      </div>
    </div>
  );
}

function AgreeButton({
  districtId,
  postId,
  uid,
  initialCount,
  fullWidth = false,
}: {
  districtId: string;
  postId: string;
  uid: string | null;
  initialCount: number;
  fullWidth?: boolean;
}) {
  const { state, toggle } = useAgree(districtId, postId, uid);
  const isAgreed = state.isAgreed === true;
  const disabled = !uid || state.loading;

  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await toggle();
        } catch {
          // useAgree 内で rollback 済
        }
      }}
      disabled={disabled}
      aria-pressed={isAgreed}
      aria-label={isAgreed ? 'そう思う！を解除' : 'そう思う！'}
      className={`${fullWidth ? 'w-full' : 'shrink-0 self-center'} px-3 py-2 rounded-lg text-sm font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed ${
        isAgreed
          ? 'bg-primary-600 text-white border-primary-600'
          : 'bg-white text-slate-600 border-slate-300 hover:border-primary-400'
      }`}
    >
      👍 {initialCount}
    </button>
  );
}
